/**
 * Does the viewport match a media query right now — and keep matching?
 *
 * The one place a layout decision is made in JS rather than CSS: the skill
 * editor's columns become **tabs** below `xl`, and tabs need state (which one
 * is open), so the breakpoint has to be readable from React. The query string
 * is the same one the stylesheet uses, so the two cannot disagree.
 *
 * `useSyncExternalStore` rather than an effect: the first render already knows
 * the answer, so the editor never paints three columns and then snaps to tabs.
 */
import { useCallback, useSyncExternalStore } from "react";

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );
  const read = useCallback(
    () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false),
    [query],
  );
  return useSyncExternalStore(subscribe, read, () => false);
}

/** The old web's `xl` cutoff: below it the editor's columns become tabs. */
export const EDITOR_TABS_QUERY = "(max-width: 1280px)";
